import {
  useVisibleProcessIds,
  useInterval,
} from "utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useServiceStatus } from "components/SimpleComponents/TopBanner/useServiceStatus";
import { DATA_DUMP_AGGREGATE_QUERY } from "providers/MidgardProvider";
import useQueryWithErrorHandling from "utils/useQuery";
import { useGeForDataDumpQueryHubletView } from "components/Dashboard/HubletView/hubletViewUtils";
import { midgardClients } from "@raven/provisioning";
import { useRef, useState } from "react";
import { getIsCypressRunning, useMount } from "constants/constants";
import { ONE_MINUTE_OF_MS } from "utils/timeUtils";
import { useTraceToHoneycomb } from "tracer";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useAuth0 } from "contexts/AuthManager/Auth";
import { random } from "lodash";
import { useWorkScheduleConfigJsonListAtom } from "store/atoms";

const INITIAL_RETRY_DELAY = 2 * 1000;
const MAX_RETRY_DELAY = ONE_MINUTE_OF_MS; // check at least every minute

/** check midgard status by fetching full-range DDA for first visible process */
export function MidgardStatusChecker() {
  const [toggle, setToggle] = useState(false);
  const attempts = useRef(0); // since MidgardStatusChecker unmounts when breaker closes, this will reset to 0 when the breaker opens again
  const timer = useRef(0);
  const isStorybook = useIsStorybook();
  const traceToHoneycomb = useTraceToHoneycomb({
    sampleRateOverride: isStorybook ? 0 : 1,
  });
  const ckey = useCustomerKey();
  const { user } = useAuth0();
  // as long as the breaker is open (i.e. this component is rendered), trace to honeycomb that the breaker is still open -> so we can alert when it's been open for e.g. >10 minutes
  useInterval({
    callback: () => {
      traceToHoneycomb({
        dataToTrace: {
          name: "Flight Midgard Breaker Open",
          email: String(user?.email),
          ckey,
          latestError: "MidgardStatusChecker - breaker still open",
          url: `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`,
        },
      });
    },
    immediate: false,
    delay: ONE_MINUTE_OF_MS,
  });

  // check midgard status with exponential backoff
  useMount(() => {
    /** un/re-mount MidgardStatusQuery to fire the query again */
    function retry() {
      console.log(
        `🔁 MidgardStatusChecker -> retrying... (attempt ${
          attempts.current
        }, next attempt in ${getNextDelay(attempts.current) / 1000}s)`
      );
      setToggle(false);
      window.setTimeout(() => {
        setToggle(true);
      });
    }

    /** retry, then set a timer to rerun retryLoop */
    function retryLoop() {
      retry();
      const nextDelay = getNextDelay(attempts.current);
      attempts.current += 1;
      timer.current = window.setTimeout(retryLoop, nextDelay);
    }

    retryLoop();

    return () => {
      window.clearTimeout(timer.current);
    };
  });

  return toggle ? <MidgardStatusQuery /> : null;
}

/** check Midgard DDA endpoint status, and close breaker once recovered
 * (query for a single DDA for the current shift range, similar to the one used in the dashboard)
 */
function MidgardStatusQuery() {
  const [
    {
      midgard: { breakerOpen, showBannerTimer },
    },
    setServicesStatus,
  ] = useServiceStatus();
  const visibleProcessIds = useVisibleProcessIds();
  const [workScheduleConfig] = useWorkScheduleConfigJsonListAtom();
  /** to check Midgard status we just need any old process id for the tenant */
  const firstKnownProcessId =
    visibleProcessIds[0] ??
    workScheduleConfig[0]?.processes[0]?.taps[0]?.machemetryId;
  const geForDataDumpQuery = useGeForDataDumpQueryHubletView();
  const ckey = useCustomerKey();
  const variables = {
    ge: String(geForDataDumpQuery),
    lt: String(
      geForDataDumpQuery + 1000 // just check for 1s since we only care about the status
    ),
    machemetryIds: [firstKnownProcessId],
  };
  useQueryWithErrorHandling("MIDGARD_STATUS_CHECK", DATA_DUMP_AGGREGATE_QUERY, {
    client: midgardClients.get(ckey),
    variables,
    onCompleted: data => {
      // if midgard recovered quickly before the breaker opened, cancel the show-the-banner timer
      if (showBannerTimer) {
        console.log("🔁 MidgardStatusChecker -> 🛫 quick recovery!!", data);
        window.clearTimeout(showBannerTimer);
        setServicesStatus(prev => ({
          ...prev,
          midgard: {
            ...prev.midgard,
            breakerOpen: false,
            degraded: false,
            showBannerTimer: null,
          },
        }));
        // otherwise, close the breaker, hide the banner
      } else if (breakerOpen) {
        console.log("🔁 MidgardStatusChecker -> 🛫 back online!", data);
        // jitter the recovery so we don't spam midgard all at once
        const jitter = getIsCypressRunning()
          ? 0
          : random(0, 3 * ONE_MINUTE_OF_MS); // 0-3min jitter
        console.log(
          `🔁 MidgardStatusChecker -> 🛫 jittering recovery by ${jitter}ms`
        );
        setTimeout(() => {
          setServicesStatus(prev => ({
            ...prev,
            midgard: {
              ...prev.midgard,
              breakerOpen: false,
              degraded: false,
              showBannerTimer: null,
            },
          }));
        }, jitter);
      }
    },
  });
  return null;
}

function getNextDelay(attempts: number) {
  return Math.min(MAX_RETRY_DELAY, INITIAL_RETRY_DELAY * 2 ** attempts);
}
